import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCrown } from 'react-icons/fa';
import AchievementCoin from './AchievementCoin';
import ProFeatureModal from './ProFeatureModal';
import api from '../services/apiService';
import '../scss/matchReportMentions.scss';

const MatchReportMentions = ({ playerId, isPro = false, isManager = false }) => {
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProModal, setShowProModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMentions = async () => {
      try {
        console.log('Fetching mentions for player:', playerId);
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_player_match_mentions.php?player_id=${playerId}`);
        console.log('API response:', response.data);
        if (response.data.success) {
          setMentions(response.data.mentions);
          console.log('Mentions set:', response.data.mentions);
        }
      } catch (error) {
        console.error('Error fetching match report mentions:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSubscriptionStatus = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const teamId = sessionStorage.getItem('selected_team_id');
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          setSubscriptionStatus(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    if (playerId) {
      fetchMentions();
    }
    fetchSubscriptionStatus();
  }, [playerId]);

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat('en-GB', options).format(new Date(dateString));
  };

  const handleMentionClick = (matchId) => {
    navigate(`/match/${matchId}/report`);
  };

  if (loading) {
    return (
      <div className="match-report-mentions">
        <h2 className="section-title">Match Report Mentions</h2>
        <div className="mentions-loading">Loading mentions...</div>
      </div>
    );
  }

  if (!mentions || mentions.length === 0) {
    return (
      <div className="match-report-mentions">
        <h2 className="section-title">Match Report Mentions</h2>
        <div className="no-mentions">
          <p>No match report mentions yet. Mentions will appear here when coaches highlight your performance in match reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="match-report-mentions">
      <h2 className="section-title">Match Report Mentions</h2>
      {/* Use API response data for Pro status instead of isPro prop for more accurate results */}
      {(() => {
        const isProFromAPI = subscriptionStatus?.is_pro === true || subscriptionStatus?.has_active_subscription === true;
        const effectivePro = isProFromAPI || isManager;
        return !effectivePro && mentions.length > 0 && (
          <div className="pro-upgrade-notice" onClick={() => setShowProModal(true)}>
            <FaCrown className="crown-icon" />
            <span>Upgrade to Pro to View All Match Report Mentions</span>
          </div>
        );
      })()}
      <div className="highlights-three-column">
        {mentions.map((mention, index) => {
          // Use API response data for Pro status instead of isPro prop for more accurate results
          const isProFromAPI = subscriptionStatus?.is_pro === true || subscriptionStatus?.has_active_subscription === true;
          const effectivePro = isProFromAPI || isManager;
          return (
            <div 
              key={mention.mention_id} 
              className={`highlight-item ${!effectivePro && index > 0 ? 'blurred' : ''}`}
              onClick={() => handleMentionClick(mention.match_id)}
            >
              <div className="match-info-header">
                <div className="match-date">{formatDate(mention.match_date)}</div>
                <div className="match-opponent">
                  {mention.score_white} - {mention.score_colour}
                </div>
                <div className="match-opposition">{mention.opposition ? `vs ${mention.opposition}` : 'vs Opposition'}</div>
              </div>
              
              {mention.achievement ? (
                <div className="highlight-with-award">
                  <div className="coin-container">
                    <AchievementCoin 
                      achievementId={mention.achievement} 
                      size="medium" 
                      className="highlight-coin"
                    />
                  </div>
                  <div className="highlight-info">
                    <div className="achievement-title">
                      {(() => {
                        const PLAYER_ACHIEVEMENTS = {
                          'Performance-Based': [
                            { id: 'goal-machine', label: 'Goal Machine' },
                            { id: 'playmaker', label: 'Playmaker' },
                            { id: 'defensive-rock', label: 'Defensive Rock' },
                            { id: 'safe-hands', label: 'Safe Hands' },
                            { id: 'tireless-engine', label: 'Tireless Engine' },
                            { id: 'skill-star', label: 'Skill Star' }
                          ],
                          'Effort and Attitude': [
                            { id: '100-percent', label: '100% Effort' },
                            { id: 'most-improved', label: 'Most Improved' },
                            { id: 'team-first', label: 'Team First' },
                            { id: 'bounceback', label: 'Bounceback Award' },
                            { id: 'never-gave-up', label: 'Never Gave Up' }
                          ],
                          'Leadership & Behaviour': [
                            { id: 'captains-mindset', label: 'Captain\'s Mindset' },
                            { id: 'respect-award', label: 'Respect Award' },
                            { id: 'positive-voice', label: 'Positive Voice' }
                          ]
                        };
                        
                        for (const [, awards] of Object.entries(PLAYER_ACHIEVEMENTS)) {
                          const award = awards.find(a => a.id === mention.achievement);
                          if (award) {
                            return award.label;
                          }
                        }
                        return '';
                      })()}
                    </div>
                  </div>
                  <div className="highlight-comment">{mention.comment}</div>
                  <button className="read-report-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleMentionClick(mention.match_id);
                  }}>
                    Read Match Report
                  </button>
                </div>
              ) : (
                <div className="highlight-text-only">
                  <div className="highlight-comment">{mention.comment}</div>
                  <button className="read-report-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleMentionClick(mention.match_id);
                  }}>
                    Read Match Report
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ProFeatureModal 
        isOpen={showProModal} 
        onClose={() => setShowProModal(false)}
        featureName="Match Report Mentions"
      />
    </div>
  );
};

export default MatchReportMentions; 